import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { History, RotateCcw, Eye, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CodeBlock } from '@/components/docs/CodeBlock';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export interface VersionRecord {
  id: string;
  standard_id: string;
  version: string;
  schema: object;
  example: object;
  fields: object[];
  changelog: string | null;
  published_at: string;
  published_by: string | null;
  is_current: boolean;
}

interface VersionHistoryProps {
  standardId: string;
  currentVersion: string;
  onRollback: (version: VersionRecord) => void;
  onRefresh?: () => void;
}

export function VersionHistory({ 
  standardId, 
  currentVersion, 
  onRollback,
  onRefresh 
}: VersionHistoryProps) {
  const { toast } = useToast();
  const [versions, setVersions] = useState<VersionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set());
  const [viewingVersion, setViewingVersion] = useState<VersionRecord | null>(null);
  const [rollbackConfirmOpen, setRollbackConfirmOpen] = useState(false);
  const [selectedRollback, setSelectedRollback] = useState<VersionRecord | null>(null);
  const [settingCurrent, setSettingCurrent] = useState<string | null>(null);

  useEffect(() => {
    if (standardId) {
      fetchVersions();
    }
  }, [standardId]);

  const fetchVersions = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('data_standard_versions')
      .select('*')
      .eq('standard_id', standardId)
      .order('published_at', { ascending: false });

    if (error) {
      toast({ title: 'Error fetching versions', description: error.message, variant: 'destructive' });
    } else {
      setVersions(data?.map(v => ({
        id: v.id,
        standard_id: v.standard_id,
        version: v.version,
        schema: v.schema as object,
        example: v.example as object,
        fields: v.fields as object[],
        changelog: v.changelog,
        published_at: v.published_at,
        published_by: v.published_by,
        is_current: v.is_current,
      })) || []);
    }
    setLoading(false);
  };

  const toggleExpanded = (versionId: string) => {
    setExpandedVersions(prev => {
      const next = new Set(prev);
      if (next.has(versionId)) {
        next.delete(versionId);
      } else {
        next.add(versionId);
      }
      return next;
    });
  };

  const handleSetCurrent = async (version: VersionRecord) => {
    setSettingCurrent(version.id);

    // Mark all as not current
    await supabase
      .from('data_standard_versions')
      .update({ is_current: false })
      .eq('standard_id', standardId);

    // Mark selected as current
    const { error } = await supabase
      .from('data_standard_versions')
      .update({ is_current: true })
      .eq('id', version.id);

    if (error) {
      toast({ title: 'Error updating version', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: `v${version.version} is now the current version` });
      fetchVersions();
      onRefresh?.();
    }
    setSettingCurrent(null);
  };

  const confirmRollback = (version: VersionRecord) => {
    setSelectedRollback(version);
    setRollbackConfirmOpen(true);
  };

  const executeRollback = () => {
    if (selectedRollback) {
      onRollback(selectedRollback);
      toast({ 
        title: `Version v${selectedRollback.version} restored to editor`, 
        description: 'Save to apply changes.' 
      });
    }
    setRollbackConfirmOpen(false);
    setSelectedRollback(null);
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <History className="h-4 w-4 mr-2 animate-spin" />
        Loading versions...
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="border-2 border-dashed rounded-lg p-6 text-center">
        <History className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
        <p className="text-muted-foreground">No versions published yet</p>
        <p className="text-xs text-muted-foreground mt-1">
          Publish your first version to start tracking changes.
        </p>
      </div>
    );
  }

  return (
    <>
      <ScrollArea className="h-[350px] pr-4">
        <div className="space-y-2">
          {versions.map((version) => (
            <Collapsible
              key={version.id}
              open={expandedVersions.has(version.id)}
              onOpenChange={() => toggleExpanded(version.id)}
            >
              <div className="border rounded-lg overflow-hidden">
                <CollapsibleTrigger className="w-full p-3 text-left hover:bg-muted/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {expandedVersions.has(version.id) ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                      <Badge variant={version.is_current ? "default" : "secondary"}>
                        v{version.version}
                      </Badge>
                      {version.is_current && (
                        <Badge className="bg-green-500/10 text-green-600 border border-green-500/20">
                          Current
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(version.published_at, 'datetime')}
                    </span>
                  </div>
                  {version.changelog && (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-1 pl-6">
                      {version.changelog}
                    </p>
                  )}
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <div className="border-t px-3 py-3 space-y-3 bg-muted/30">
                    {version.changelog && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Changelog</p>
                        <p className="text-sm">{version.changelog}</p>
                      </div>
                    )}
                    
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewingVersion(version);
                        }}
                      >
                        <Eye className="h-3.5 w-3.5 mr-1.5" />
                        View
                      </Button>
                      
                      {!version.is_current && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetCurrent(version);
                            }}
                            disabled={settingCurrent === version.id}
                          >
                            <Check className="h-3.5 w-3.5 mr-1.5" />
                            Set Current
                          </Button>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmRollback(version);
                            }}
                          >
                            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                            Restore to Editor
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
        </div>
      </ScrollArea>

      {/* View Version Dialog */}
      <Dialog open={!!viewingVersion} onOpenChange={() => setViewingVersion(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Version {viewingVersion?.version}
              {viewingVersion?.is_current && (
                <Badge className="ml-2 bg-green-500/10 text-green-600">Current</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="schema" className="flex-1 overflow-hidden">
            <TabsList>
              <TabsTrigger value="schema">Schema</TabsTrigger>
              <TabsTrigger value="example">Example</TabsTrigger>
              <TabsTrigger value="fields">Fields</TabsTrigger>
            </TabsList>
            <div className="overflow-auto flex-1 mt-4">
              <TabsContent value="schema" className="m-0">
                <CodeBlock
                  code={JSON.stringify(viewingVersion?.schema || {}, null, 2)}
                  language="json"
                  showLineNumbers
                />
              </TabsContent>
              <TabsContent value="example" className="m-0">
                <CodeBlock
                  code={JSON.stringify(viewingVersion?.example || {}, null, 2)}
                  language="json"
                  showLineNumbers
                />
              </TabsContent>
              <TabsContent value="fields" className="m-0">
                <CodeBlock
                  code={JSON.stringify(viewingVersion?.fields || [], null, 2)}
                  language="json"
                  showLineNumbers
                />
              </TabsContent>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Rollback Confirmation */}
      <AlertDialog open={rollbackConfirmOpen} onOpenChange={setRollbackConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore Version v{selectedRollback?.version}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will copy the schema, example, and fields from v{selectedRollback?.version} 
              into the editor. You'll need to save to apply the changes. This won't affect 
              any published versions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeRollback}>
              Restore to Editor
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
